import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { differenceInDays, format, parseISO, isValid } from 'date-fns'
import type { ExpiryStatus, AmountFrequency } from '@/types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function expiryStatus(expiryDate?: string): ExpiryStatus {
  if (!expiryDate) return 'none'
  const expiry = parseISO(expiryDate)
  if (!isValid(expiry)) return 'none'
  const days = differenceInDays(expiry, new Date())
  if (days < 0) return 'expired'
  if (days <= 7) return 'critical'
  if (days <= 30) return 'warning'
  if (days <= 90) return 'upcoming'
  return 'ok'
}

export function daysUntilExpiry(expiryDate?: string): number | null {
  if (!expiryDate) return null
  const expiry = parseISO(expiryDate)
  if (!isValid(expiry)) return null
  return differenceInDays(expiry, new Date())
}

export function formatDate(date?: string): string {
  if (!date) return '—'
  const parsed = parseISO(date)
  if (!isValid(parsed)) return '—'
  return format(parsed, 'd MMM yyyy')
}

export function formatCurrency(amount?: number): string {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount)
}

export function annualAmount(amount?: number, frequency?: AmountFrequency): number {
  if (!amount || !frequency) return 0
  const multipliers: Record<AmountFrequency, number> = {
    once: 0,
    weekly: 52,
    fortnightly: 26,
    monthly: 12,
    quarterly: 4,
    annual: 1,
  }
  return amount * (multipliers[frequency] ?? 0)
}

export function formatFileSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function generateId(): string {
  return crypto.randomUUID()
}
